class VariantCardCount {
  static countGridItems(doc = document) {
    const grid = doc.getElementById('product-grid');
    if (!grid) return 0;

    if (grid.tagName === 'UL') {
      return grid.querySelectorAll(':scope > .grid__item.product__item, :scope > .grid__item.product').length;
    }

    return grid.querySelectorAll('ul .grid__item').length;
  }

  // The search page <title> carries Shopify's product-level result count (e.g.
  // "Search: 27 results found for ..."). With variants shown as cards the page
  // reports a different number, so swap the native count in the title for the
  // card total. The base title and native count are read once, from the first
  // render, because AJAX filter/sort changes never touch the title themselves.
  static updateDocumentTitle(total) {
    const grid = document.getElementById('product-grid');
    if (!grid || grid.dataset.showVariantsAsCards !== 'true') return;

    if (this.titleBase === undefined) {
      const nativeCount = grid.dataset.searchResultsCount;
      this.titleBase = nativeCount ? document.title : null;
      this.titleNativeCount = nativeCount || null;
    }
    if (!this.titleBase || total === null || total === undefined) return;

    const pattern = new RegExp(`(^|\\D)${this.titleNativeCount}(?!\\d)`);
    if (pattern.test(this.titleBase)) {
      document.title = this.titleBase.replace(pattern, `$1${total}`);
    }
  }

  static updateTotalElements(total, allTotal) {
    this.updateDocumentTitle(total);

    document.querySelectorAll('[data-variant-card-total]').forEach((element) => {
      element.dataset.variantCardTotal = String(total);
      if (element.classList.contains('total_count')) {
        element.textContent = total;
      }
    });

    const productCount = document.getElementById('ProductCount');
    const productCountDesktop = document.getElementById('ProductCountDesktop');

    [productCount, productCountDesktop].forEach((element) => {
      if (!element) return;

      const template = element.dataset.countTemplate;
      const needsAllCount = template && template.includes('__ALL_COUNT__');
      const canRender = template && (!needsAllCount || (allTotal !== null && allTotal !== undefined));

      if (canRender) {
        let html = template.replace('__COUNT__', total);
        if (needsAllCount) html = html.replace('__ALL_COUNT__', allTotal);
        element.innerHTML = html;
      }

      // Reveal even when the template couldn't be resolved (e.g. a failed fetch)
      // so the element never stays hidden — it falls back to its initial markup.
      element.classList.remove('js-count-pending');
    });
  }

  static updateLoadedElements(loaded, total) {
    document.querySelectorAll('.loaded_count').forEach((element) => {
      element.textContent = loaded;
    });

    const progressBar = document.querySelector('.load-more_progress-bar .progress-value');
    if (progressBar && total > 0) {
      progressBar.style.width = `${Math.min((loaded / total) * 100, 100)}%`;
    }
  }

  static getNextPageUrl(productGrid) {
    const nextUrl = productGrid?.dataset.nextUrl;
    if (!nextUrl) return null;
    const url = new URL(nextUrl, window.location.origin);
    // Ask for just the grid section instead of the whole page: far cheaper to
    // render, and the count only needs the cards.
    if (productGrid.dataset.id && !url.searchParams.has('section_id')) {
      url.searchParams.set('section_id', productGrid.dataset.id);
    }
    return url.href;
  }

  // Counts never depend on sort order or page, so both are stripped when building
  // a cache key / count URL. That way switching Sort By reuses totals that were
  // already resolved instead of re-fetching every facet value and page.
  static normalizeCountUrl(url) {
    const parsed = new URL(url, window.location.origin);
    parsed.searchParams.delete('sort_by');
    parsed.searchParams.delete('page');
    parsed.searchParams.delete('section_id');
    return `${parsed.pathname}${parsed.search}`;
  }

  // Same URL as getNextPageUrl, but for an explicit page number.
  static getPageUrl(productGrid, page) {
    const nextUrl = this.getNextPageUrl(productGrid);
    if (!nextUrl) return null;
    const url = new URL(nextUrl);
    url.searchParams.set('page', String(page));
    return url.href;
  }

  static async fetchPageCount(url) {
    try {
      const response = await fetch(url);
      if (!response.ok) return 0;

      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      return this.countGridItems(doc);
    } catch (error) {
      console.error('Unable to resolve paged card total', error);
      return 0;
    }
  }

  // Walks from an already-fetched (grid, doc) pair across the remaining pages,
  // summing real rendered card counts. Side-effect free — safe to use against a
  // hypothetical/other URL's document, not just the live page.
  //
  // When the grid advertises its page count (data-total-pages) the remaining
  // pages are fetched in parallel, so the walk costs one round trip however many
  // pages there are. Without it, falls back to following data-next-url one page
  // at a time.
  static async walkTotal(grid, doc, { maxPages = Infinity } = {}) {
    let total = this.countGridItems(doc);
    const totalPages = parseInt(grid?.dataset.totalPages || '0', 10);
    const currentPage = parseInt(grid?.dataset.currentPage || '1', 10);

    if (totalPages > currentPage && grid.dataset.nextUrl) {
      const lastPage = Math.min(totalPages, maxPages === Infinity ? totalPages : maxPages);
      const urls = [];
      for (let page = currentPage + 1; page <= lastPage; page++) {
        urls.push(this.getPageUrl(grid, page));
      }
      const counts = await Promise.all(urls.filter(Boolean).map((url) => this.fetchPageCount(url)));
      return counts.reduce((sum, count) => sum + count, total);
    }

    let nextUrl = this.getNextPageUrl(grid);
    const visited = new Set();
    let pages = 1;

    while (nextUrl && !visited.has(nextUrl) && pages < maxPages) {
      visited.add(nextUrl);
      pages += 1;

      try {
        const response = await fetch(nextUrl);
        if (!response.ok) break;

        const html = await response.text();
        const nextDoc = new DOMParser().parseFromString(html, 'text/html');
        const nextGrid = nextDoc.getElementById('product-grid');
        if (!nextGrid) break;

        total += this.countGridItems(nextDoc);
        nextUrl = this.getNextPageUrl(nextGrid);
      } catch (error) {
        console.error('Unable to resolve paged card total', error);
        break;
      }
    }

    return total;
  }

  // Resolves the true card total for an arbitrary collection/search URL (its own
  // filters, its own pagination) by fetching it as a section and walking its
  // pages if needed. Used for facet-value recount and for the unfiltered
  // "all products" denominator alike. Cached per (url, maxPages).
  static resolveUrlTotal(rawUrl, { maxPages = Infinity } = {}) {
    const url = this.normalizeCountUrl(rawUrl);
    const cacheKey = `${url}::${maxPages}`;
    if (this.urlTotalCache.has(cacheKey)) return this.urlTotalCache.get(cacheKey);

    const sectionId = document.getElementById('product-grid')?.dataset.id;
    const sectionUrl = sectionId ? `${url}${url.includes('?') ? '&' : '?'}section_id=${sectionId}` : url;

    const promise = fetch(sectionUrl)
      .then((response) => (response.ok ? response.text() : null))
      .then((html) => {
        if (!html) return null;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const grid = doc.getElementById('product-grid');
        if (!grid) return null;

        if (grid.dataset.variantCardTotalPending === 'true') {
          return this.walkTotal(grid, doc, { maxPages });
        }

        return parseInt(grid.dataset.variantCardTotal || '0', 10);
      })
      .catch((error) => {
        console.error('Unable to resolve card total for url', url, error);
        return null;
      });

    this.urlTotalCache.set(cacheKey, promise);
    return promise;
  }

  static getCachedUrlTotal(rawUrl, { maxPages = Infinity } = {}) {
    return this.urlTotalCache.get(`${this.normalizeCountUrl(rawUrl)}::${maxPages}`) || null;
  }

  // The unfiltered "of N" denominator rarely changes, and walking every page for
  // it is the slowest part of the first count after a filter click, so keep it
  // for the rest of the session (short TTL, so catalogue edits still show up).
  static resolveAllProductsTotal(productGrid) {
    const collectionUrl = productGrid?.dataset.collectionUrl;
    if (!collectionUrl) return Promise.resolve(null);

    const stored = this.readAllProductsTotal(collectionUrl);
    if (stored !== null) return Promise.resolve(stored);

    return this.resolveUrlTotal(collectionUrl).then((total) => {
      this.storeAllProductsTotal(collectionUrl, total);
      return total;
    });
  }

  static allProductsTotalKey(collectionUrl) {
    return `variant-card-count:all:${this.normalizeCountUrl(collectionUrl)}`;
  }

  static readAllProductsTotal(collectionUrl) {
    try {
      const stored = JSON.parse(window.sessionStorage.getItem(this.allProductsTotalKey(collectionUrl)) || 'null');
      if (stored && Date.now() - stored.at < this.ALL_TOTAL_TTL && Number.isFinite(stored.total)) {
        return stored.total;
      }
    } catch (error) {
      // sessionStorage can be unavailable; the caller resolves it live instead.
    }
    return null;
  }

  static storeAllProductsTotal(collectionUrl, total) {
    if (total === null || total === undefined) return;
    try {
      window.sessionStorage.setItem(this.allProductsTotalKey(collectionUrl), JSON.stringify({ total, at: Date.now() }));
    } catch (error) {
      // ignore
    }
  }

  static countTemplatesNeedAllCount() {
    return [...document.querySelectorAll('[data-count-template]')].some((element) =>
      element.dataset.countTemplate.includes('__ALL_COUNT__')
    );
  }

  // Single entry point for both first paint and post-AJAX-filter refresh: resolves
  // the (possibly paged) numerator, resolves the unfiltered denominator only when
  // the active count template actually needs it, then updates the DOM once.
  // `url` is the filter state the grid was rendered for. It defaults to the
  // address bar, which is right on first load but can already be a newer filter
  // state by the time an older AJAX response is rendered.
  static refreshCounts(productGrid, { url } = {}) {
    this.currentRefresh = this.runRefreshCounts(productGrid, url || window.location.href);
    return this.currentRefresh;
  }

  static async runRefreshCounts(productGrid, stateUrl) {
    if (!productGrid) return;

    // Each call supersedes the previous one: a slow, older call (e.g. an earlier
    // sort or filter whose page walk is still in flight) must not overwrite the
    // numbers for the state the shopper is looking at now.
    const token = (this.refreshToken = (this.refreshToken || 0) + 1);
    const isStale = () => token !== this.refreshToken;

    const loaded = this.countGridItems(document);
    let total = parseInt(productGrid.dataset.variantCardTotal || '0', 10);

    if (productGrid.dataset.variantCardTotalPending === 'true') {
      // Totals only depend on the active filters, not on sort or page, so reuse
      // the walk for the same filters instead of re-fetching every page.
      const key = this.normalizeCountUrl(stateUrl);
      if (!this.liveTotalCache.has(key)) {
        this.liveTotalCache.set(key, this.walkTotal(productGrid, document));
      }
      total = await this.liveTotalCache.get(key);
      if (isStale()) return;
      productGrid.dataset.variantCardTotal = String(total);
      productGrid.dataset.variantCardTotalPending = 'false';
    }

    // On the unfiltered page the live total IS the "of N" denominator, so keep it
    // for the first filter click instead of walking every page again then.
    const collectionUrl = productGrid.dataset.collectionUrl;
    if (collectionUrl && this.normalizeCountUrl(stateUrl) === this.normalizeCountUrl(collectionUrl)) {
      this.storeAllProductsTotal(collectionUrl, total);
    }

    this.updateLoadedElements(loaded, total);

    const allTotal = this.countTemplatesNeedAllCount()
      ? await this.resolveAllProductsTotal(productGrid)
      : null;
    if (isStale()) return;

    this.updateTotalElements(total, allTotal);
  }

  static init() {
    const productGrid = document.getElementById('product-grid');
    if (!productGrid || productGrid.dataset.showVariantsAsCards !== 'true') return;

    this.refreshCounts(productGrid);
  }
}

VariantCardCount.urlTotalCache = new Map();
VariantCardCount.ALL_TOTAL_TTL = 10 * 60 * 1000;
VariantCardCount.currentRefresh = null;
VariantCardCount.liveTotalCache = new Map();

document.addEventListener('DOMContentLoaded', () => {
  VariantCardCount.init();
});

if (typeof window !== 'undefined') {
  window.VariantCardCount = VariantCardCount;
}
